// ─────────────────────────────────────────────
//  Dashboard.jsx — bottom app bar (Aug 2025)
//  Refactored: tabs and shared UI live in ./dashboard/ (BooksyAppExpo-style).
//  Prod-safe: CSRF, sockets, booking intent, image loading.
// ─────────────────────────────────────────────
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { FaHome, FaHistory, FaUser, FaBell, FaSignOutAlt, FaBars } from 'react-icons/fa';
import './Dashboard.css';
import { http, SOCKET_URL } from '../lib/http';
import { useI18n } from '../contexts/I18n';
import { useAuth } from '../contexts/AuthContext';
import { getBookingIntent, clearBookingIntent } from '../utils/bookingIntent';
import {
  HomeTab,
  HistoryTab,
  NotificationsTab,
  ProfileTab,
  Sidebar,
  ReviewModal,
  TabLoadingBuffer,
  SuccessBanner,
} from './dashboard/index.js';
import { shouldShowProfileEmailVerification, markVerifyForBooking } from '../utils/emailVerification';
import ProfileEditForm from './dashboard/ProfileEditForm';
import ProfilePasswordForm from './dashboard/ProfilePasswordForm';
import { useSwipeNavigation, useDashboardBooking } from './dashboard/index.js';
import { markReviewShown, shouldAutoShowReview, xsrf } from './dashboard/utils';
import { HAS_SPECIAL_CHAR_RE } from '../utils/passwordPolicy';

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user: authUser, refreshAuth } = useAuth();
  /* ────────── state buckets ────────── */
  const [tab, setTab] = useState('ehgezly');
  const [bookingRetryToken, setBookingRetryToken] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.verifyForBooking) {
      markVerifyForBooking();
      setTab('profile');
      window.history.replaceState({}, document.title, location.pathname);
    } else if (location.state?.openProfile) {
      setTab('profile');
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.state?.verifyForBooking, location.state?.openProfile, location.pathname]);
  
  // Mobile swipe navigation: only moves inside dashboard tabs.
  // Swiping up to open /explore felt like "scroll" was randomly
  // changing screens in production, so we disable that behavior
  // and keep a simple swipe-down to open History instead.
  const swipeHandlers = useSwipeNavigation(
    () => {},
    () => { if (window.innerWidth <= 768) setTab('history'); }
  );

  const [profile,  setProfile]  = useState(null);
  const [booking,  setBooking]  = useState(null);    // only "current" booking
  const [history,  setHistory]  = useState([]);      // all user bookings
  const [reviewTarget, setReviewTarget] = useState(null); // completed & unreviewed booking
  const [forceShowDetails, setForceShowDetails] = useState(false);
  const [focusBookingId, setFocusBookingId] = useState('');
  const deepLinkHandled = useRef(false);

  const [busy, setBusy] = useState({ profile:true, booking:true, history:true });
  const [err,  setErr]  = useState({ profile:'',   booking:'',   history:''   });

  /* profile-edit */
  const [edit,  setEdit]  = useState(false);
  const [editPassword, setEditPassword] = useState(false);
  const [draft, setDraft] = useState(null);
  const [avatar,setAvatar]= useState(null);
  const [saving,setSaving]= useState(false);
  const [msg,   setMsg]   = useState({ ok:'', fail:'' });
  const successMsgTimeoutRef = useRef(null);
  useEffect(() => () => {
    if (successMsgTimeoutRef.current) clearTimeout(successMsgTimeoutRef.current);
  }, []);

  /* password-change (setter used in saveProfile to reset UI state) */
  const [_showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPwd: '', newPwd: '', confirmPwd: '' });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordVisibility, setPasswordVisibility] = useState({ currentPwd: false, newPwd: false, confirmPwd: false });
  const [changingPassword, setChangingPassword] = useState(false);

  /* review-modal state */
  const [showReview, setShowReview] = useState(false);
  const [stars,      setStars]      = useState(0);
  const [comment,    setComment]    = useState('');

  /* ────────── notifications (user-specific from server, no localStorage) ────────── */
  const [notifications, setNotifications] = useState([]);
  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  const [toast, setToast] = useState(null); // { text, type: 'success' | 'warn' | 'info' }
  const addNotification = useCallback((text, type = 'info', sourceId) => {
    setNotifications((list) => {
      const now = Date.now();
      const isDup = list.some((n) => {
        if (sourceId && n.sourceId && n.sourceId === sourceId) return true;
        const sameText = n.text === text;
        const withinHour = (now - new Date(n.ts).getTime()) < 60 * 60 * 1000;
        return sameText && withinHour;
      });
      if (isDup) return list;
      const item = {
        id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        title: '',
        text,
        type,
        read: false,
        ts: new Date().toISOString(),
        ...(sourceId ? { sourceId } : {}),
      };
      return [item, ...list].slice(0, 100);
    });
    setToast({ text, type });
    window.clearTimeout(addNotification._t);
    addNotification._t = window.setTimeout(() => setToast(null), 6000);
  }, []);
  const addNotificationRef = useRef(addNotification);
  addNotificationRef.current = addNotification;

  // Push a notification for relevant statuses (socket or initial load)
  const notifyForStatus = useCallback((doc) => {
    const status = String(doc?.status || '').toLowerCase();
    const interesting = ['accepted','approved','declined','rejected','cancelled','canceled','completed'];
    if (!interesting.includes(status)) return;

    const when = [doc?.selectedDate || doc?.date, doc?.selectedHour || doc?.time].filter(Boolean).join(' ');
    const shop = doc?.shopName || 'the shop';

    const msg =
      status === 'accepted' || status === 'approved'
        ? `Your appointment at ${shop}${when ? ` (${when})` : ''} was accepted.`
        : status === 'completed'
        ? `Your appointment at ${shop}${when ? ` (${when})` : ''} is completed.`
        : `Your appointment at ${shop}${when ? ` (${when})` : ''} was ${status}.`;

    const src = `booking:${doc?._id}:${status}`;
    addNotificationRef.current(
      msg,
      (status === 'accepted' || status === 'approved' || status === 'completed') ? 'success' : 'warn',
      src
    );
  }, []);

  /* new-user "intent" booking */
  const { state } = useLocation();
  const intentFromState = state?.bookingDetails;
  const intentFromStorage = getBookingIntent();
  const intent = intentFromState || intentFromStorage;

  useEffect(() => {
    http.fetch('/api/csrf', { method: 'GET', cache: 'no-store' }).catch(() => {});
  }, []);

  /* ────────── fetch profile once ────────── */
  const loadProfile = useCallback(async () => {
      try {
        setErr(s => ({ ...s, profile: '' }));
        const p = await http.getJson('/api/profile');
      setProfile(p); 
      setDraft(p);
      } catch (e) {
        const unauthorized = e.status === 401 || /unauthorized/i.test(e.message || '');
        setErr(s => ({
          ...s,
          profile: unauthorized ? 'UNAUTHORIZED' : e.message,
        }));
        if (!unauthorized && authUser?.email) {
          const fallback = {
            name: authUser.name || '',
            email: authUser.email || '',
            phone: authUser.phone || '',
            emailVerified: authUser.emailVerified,
            isEmailVerified: authUser.isEmailVerified,
            isPhoneVerified: authUser.isPhoneVerified,
          };
          setProfile(fallback);
          setDraft(fallback);
        } else if (unauthorized) {
          setProfile(null);
          setDraft(null);
        }
      } finally {
        setBusy(s => ({ ...s, profile:false }));
      }
  }, [authUser?.email]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  /* ────────── load notifications (user-specific from API) ────────── */
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const loadNotifications = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      const items = await http.getJson('/api/notifications/inapp');
      const serverItems = Array.isArray(items) ? items.map((it) => ({
        id: String(it.id || it._id || ''),
        title: it.title || '',
        text: it.message || it.text || (it.title && it.message ? `${it.title}: ${it.message}` : it.title || it.message || ''),
        type: 'info',
        read: Boolean(it.read),
        ts: it.ts || it.createdAt || new Date().toISOString(),
        sourceId: `server:${it.id || it._id || ''}`,
      })) : [];
      setNotifications((prev) => {
        const clientItems = prev.filter((n) => n.sourceId && n.sourceId.startsWith('booking:'));
        const serverIds = new Set(serverItems.map((m) => m.id));
        const merged = [...serverItems];
        for (const c of clientItems) {
          if (!serverIds.has(c.id)) merged.push(c);
        }
        merged.sort((a, b) => new Date(b.ts) - new Date(a.ts));
        return merged.slice(0, 100);
      });
    } catch (_) {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Refresh notifications when switching to notification tab
  useEffect(() => {
    if (tab === 'notification') {
      loadNotifications();
    }
  }, [tab, loadNotifications]);

  const [selectedNotification, setSelectedNotification] = useState(null);

  const markNotificationRead = useCallback(async (id) => {
    const notif = notifications.find((n) => n.id === id);
    const isServerItem = notif?.sourceId?.startsWith('server:');
    setNotifications((list) =>
      list.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    if (isServerItem) {
      try {
        await http.postJson(`/api/notifications/${id}/read`);
      } catch (e) {
        setNotifications((list) =>
          list.map((n) => (n.id === id ? { ...n, read: false } : n))
        );
      }
    }
  }, [notifications]);

  const markAllNotificationsRead = useCallback(async () => {
    setNotifications((list) => list.map((n) => ({ ...n, read: true })));
    try {
      await http.postJson('/api/notifications/mark-all-read');
      await loadNotifications();
    } catch (e) {
      await loadNotifications();
    }
  }, [loadNotifications]);

  const clearAllNotifications = useCallback(() => {
    const msg = t('clearNotificationsConfirm') || 'Are you sure you want to clear all notifications?';
    if (!window.confirm(msg)) return;
    const doClear = async () => {
      try {
        await http.postJson('/api/notifications/clear-all');
        setNotifications([]);
        setSelectedNotification(null);
        setToast({ text: t('allNotificationsCleared') || 'All notifications cleared', type: 'success' });
        window.setTimeout(() => setToast(null), 4000);
      } catch (e) {
        setToast({ text: t('dashError') || 'Something went wrong', type: 'warn' });
        window.setTimeout(() => setToast(null), 4000);
      }
    };
    doClear();
  }, [t]);

  const handleEmailVerified = useCallback(async () => {
    await loadProfile();
    await refreshAuth();
    if (getBookingIntent()) {
      setBookingRetryToken((n) => n + 1);
      setTab('ehgezly');
      setToast({
        text: lang === 'ar' ? 'تم التحقق — جاري إتمام حجزك...' : 'Verified — completing your booking...',
        type: 'success',
      });
      window.setTimeout(() => setToast(null), 4000);
    }
  }, [refreshAuth, lang]);

  const { loadBooking } = useDashboardBooking({
    setBooking,
    setBusy,
    setErr,
    booking,
    busyBooking: busy.booking,
    intent,
    intentFromStorage,
    clearBookingIntent,
    notifyForStatus,
    locationPathname: location.pathname,
    bookingRetryToken,
    onEmailVerificationRequired: () => {
      markVerifyForBooking();
      setTab('profile');
    },
  });

  /* ────────── load history ────────── */
  useEffect(() => {
    (async () => {
      try {
        const h = await http.getJson('/api/bookings');
        const arr = Array.isArray(h) ? h : h.data || [];
        arr.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
        setHistory(arr);
      } catch (e) {
        setErr(s => ({
          ...s,
          history: (e.status === 401 || /unauthorized/i.test(e.message || '')) ? 'UNAUTHORIZED' : e.message,
        }));
      } finally {
        setBusy(s=>({ ...s, history:false }));
      }
    })();
  }, []);

  /* sockets (Safari-safe: send Bearer if present) */
  useEffect(() => {
    const sock = io(SOCKET_URL, {
      withCredentials: true,
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      auth: (cb) => cb({ token: (http.getToken && http.getToken()) || '' }),
    });

    sock.on('booking:updated', (doc) => {
      setBooking(doc);
      notifyForStatus(doc); // ALWAYS evaluate
      if (doc?.status === 'completed' && !doc?.reviewed && shouldAutoShowReview(doc._id)) {
        markReviewShown(doc._id);
        setReviewTarget(doc);
        setShowReview(true);
      }
    });

    // Admin in-app broadcast → show in notifications tab (deduped by source)
    sock.on('notification:inapp', (payload) => {
      const t = String(payload?.title || '').trim();
      const m = String(payload?.message || '').trim();
      if (!m && !t) return;
      const text = t && m ? `${t}: ${m}` : (t || m);
      const src  = `admin:${payload?.id || ''}`;
      addNotificationRef.current(text, 'info', src);
    });

    return () => sock.disconnect();
  }, [notifyForStatus]);

  /* Show review modal only once when current booking just became completed (not on every load) */
  useEffect(() => {
    if (booking?.status !== 'completed' || booking?.reviewed || !shouldAutoShowReview(booking._id)) return;
    markReviewShown(booking._id);
    setReviewTarget(booking);
    setShowReview(true);
  }, [booking]);

  /* Email deep links: /dashboard?booking=ID opens that booking, /dashboard?review=ID opens the review screen */
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const params = new URLSearchParams(location.search || '');
    const bookingId = params.get('booking');
    const reviewId = params.get('review');
    if (!bookingId && !reviewId) return;

    const targetId = reviewId || bookingId;
    const foundInCurrent = booking && String(booking._id) === String(targetId);
    if (busy.history && !foundInCurrent) return;

    const found =
      history.find((item) => String(item._id) === String(targetId)) ||
      (booking && String(booking._id) === String(targetId) ? booking : null);

    const activeStatuses = ['pending', 'held', 'accepted', 'confirmed', 'rescheduled'];
    const isActive = found && activeStatuses.includes(found.status);

    if (reviewId) {
      deepLinkHandled.current = true;
      if (found && found.status === 'completed' && !found.reviewed) {
        setReviewTarget(found);
        setStars(0);
        setComment('');
        setShowReview(true);
      } else if (found) {
        setFocusBookingId(String(found._id));
        setTab('history');
      }
      window.history.replaceState({}, document.title, location.pathname);
      return;
    }

    deepLinkHandled.current = true;
    if (isActive) {
      setBooking(found);
      setForceShowDetails(true);
      setTab('ehgezly');
    } else {
      setFocusBookingId(String(bookingId));
      setTab('history');
    }
    window.history.replaceState({}, document.title, location.pathname);
  }, [location.search, location.pathname, history, booking, busy.history]);

  /* Mark notifications read when opening the tab - DISABLED to match BooksyAppExpo behavior */
  // useEffect(() => {
  //   if (tab === 'notification' && notifications.some(n => !n.read)) {
  //     setNotifications(list => list.map(n => ({ ...n, read: true })));
  //   }
  // }, [tab, notifications]);

  /* ────────── mutations ────────── */
  const saveProfile = async () => {
    setSaving(true); setMsg({ ok:'', fail:'' });
    try {
      const fd = new FormData();
      // Send name if it's in draft (name is editable)
      if (draft?.name !== undefined) {
        fd.append('name', String(draft.name || '').trim());
      }
      // Phone and email are read-only in the form, but send current values for consistency
      if (profile?.phone) fd.append('phone', String(profile.phone || '').trim());
      if (profile?.email) fd.append('email', String(profile.email || '').trim());
      // Only send avatar if changed
      if (avatar) fd.append('avatar', avatar);

      const r = await http.fetch('/api/profile', {
        method:'PUT',
        headers: { 'X-CSRF-Token': xsrf() },
        body:fd
      });
      if (!r.ok) throw new Error(await r.text());
      const p = await r.json();
      // Preserve existing profile data (name, phone, email) if not in response
      const updatedProfile = {
        ...profile,
        ...p,
        name: p.name || profile?.name,
        phone: p.phone || profile?.phone,
        email: p.email || profile?.email,
      };
      setProfile(updatedProfile); setDraft(updatedProfile); setAvatar(null);
      /* Keep edit view mounted so success replaces the edit form (same card size). */
      setMsg({ ok:t('profileUpdated'), fail:'' });
      if (successMsgTimeoutRef.current) clearTimeout(successMsgTimeoutRef.current);
      successMsgTimeoutRef.current = setTimeout(() => {
        setMsg((m) => ({ ...m, ok: '' }));
        setEdit(false);
      }, 5000);
    } catch (e){ setMsg({ ok:'', fail:e.message || t('failedToUpdateProfile') }); }
    finally{ setSaving(false); }
  };

  const validatePassword = () => {
    const errors = {};
    if (!passwordData.currentPwd) {
      errors.currentPwd = 'Current password is required';
    }
    if (!passwordData.newPwd) {
      errors.newPwd = 'New password is required';
    } else if (passwordData.newPwd.length < 8) {
      errors.newPwd = 'Must be at least 8 characters';
    } else if (!/(?=.*[a-z])/.test(passwordData.newPwd)) {
      errors.newPwd = 'Must contain lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(passwordData.newPwd)) {
      errors.newPwd = 'Must contain uppercase letter';
    } else if (!/(?=.*\d)/.test(passwordData.newPwd)) {
      errors.newPwd = 'Must contain number';
    } else if (!HAS_SPECIAL_CHAR_RE.test(passwordData.newPwd)) {
      errors.newPwd = 'Must contain special character';
    }
    if (!passwordData.confirmPwd) {
      errors.confirmPwd = 'Please confirm your password';
    } else if (passwordData.newPwd !== passwordData.confirmPwd) {
      errors.confirmPwd = 'Passwords do not match';
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const changePassword = async () => {
    if (!validatePassword()) {
      setMsg({ ok: '', fail: 'Please fix the password errors' });
      return;
    }

    setChangingPassword(true);
    setPasswordErrors(prev => ({ ...prev, currentPwd: null }));
    setMsg({ ok: '', fail: '' });
    try {
      const r = await http.fetch('/api/users/me/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': xsrf(),
        },
        body: JSON.stringify({
          currentPwd: passwordData.currentPwd,
          newPwd: passwordData.newPwd,
        }),
      });

      if (!r.ok) {
        const errorData = await r.json().catch(() => ({ message: 'Failed to change password' }));
        const msg = errorData?.message || errorData?.error || 'Failed to change password';
        if (r.status === 400 || r.status === 401 || r.status === 403) {
          setPasswordErrors(prev => ({ ...prev, currentPwd: msg }));
        } else {
          setMsg({ ok: '', fail: msg });
        }
        return;
      }

      setPasswordData({ currentPwd: '', newPwd: '', confirmPwd: '' });
      setPasswordErrors({});
      setShowPasswordChange(false);
      setEditPassword(false);
      setPasswordVisibility({ currentPwd: false, newPwd: false, confirmPwd: false });
      setTab('profile'); /* navigate to profile tab so success banner shows in same place as profile update */
      setMsg({ ok: 'Password updated successfully', fail: '' });
      if (successMsgTimeoutRef.current) clearTimeout(successMsgTimeoutRef.current);
      successMsgTimeoutRef.current = setTimeout(() => setMsg((m) => ({ ...m, ok: '' })), 5000);
      
      // Reload profile to ensure we have the latest data
      try {
        const p = await http.getJson('/api/profile');
        setProfile(p);
        setDraft(p);
      } catch (e) {
        console.error('Failed to reload profile after password change:', e);
      }
    } catch (e) {
      setMsg({ ok: '', fail: e.message || 'Failed to change password' });
    } finally {
      setChangingPassword(false);
    }
  };

  const cancelBooking = async () => {
    if (!booking?._id) return;
    try {
      const r = await http.fetch(`/api/bookings/${booking._id}`, {
        method:'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': xsrf(),
        },
        body: JSON.stringify({ status:'cancelled' }),
      });
      if (!r.ok) {
        const txt = await r.text().catch(()=>`HTTP ${r.status}`);
        alert(`Cancel failed: ${txt}`);
        return;
      }
      await loadBooking();
    } catch (e) {
      alert('Cancel failed — please try again.');
    }
  };

  const logout = () =>
    http.fetch('/api/logout', { method:'POST', headers: { 'X-CSRF-Token': xsrf() } })
      .finally(() => window.location.replace('/'));

  const submitReview = async () => {
    if (stars === 0) return;
    if (!reviewTarget?._id) {
      alert('No booking to review was found.');
      return;
    }
    try {
      const res = await http.fetch('/api/reviews', {
        method:'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': xsrf(),
        },
        body: JSON.stringify({
          bookingId: reviewTarget._id,
          rating   : stars,
          comment,
        }),
      });

      if (res.status === 401 || res.status === 403) {
        const go = window.confirm('Please sign in with a client account to leave a review. Go to sign in now?');
        if (go) window.location.replace('/register');
        return;
      }

      if (res.status === 409) {
        setHistory(list => list.map(b => b._id === reviewTarget._id ? { ...b, reviewed: true } : b));
        if (booking && booking._id === reviewTarget._id) setBooking(b => ({ ...b, reviewed: true }));
        setReviewTarget(null); setShowReview(false); setStars(0); setComment('');
        return;
      }

      if (!res.ok) {
        const msg = await res.text().catch(() => 'Failed to save review');
        alert(`Saving your review failed: ${msg}`);
        return;
      }

      setHistory(list => list.map(b => b._id === reviewTarget._id ? { ...b, reviewed: true } : b));
      if (booking && booking._id === reviewTarget._id) setBooking(b => ({ ...b, reviewed: true }));
      setReviewTarget(null); setShowReview(false); setStars(0); setComment('');
    } catch {
      alert('Saving your review failed – please try later.');
    }
  };

  /* ══════════════════════ TABS ══════════════════════ */
  /* HomeTab is imported from ./dashboard/HomeTab.jsx */




  /* ────────── render ────────── */
  const renderTab = () => {
    switch (tab) {
      case 'ehgezly':
        if (busy.booking) return <TabLoadingBuffer label={t('dashLoading') || 'Loading...'} />;
        return (
          <HomeTab
            booking={booking}
            busyBooking={busy.booking}
            errBooking={err.booking}
            forceShowDetails={forceShowDetails}
            onCancel={cancelBooking}
            onReview={(b) => { setReviewTarget(b); setStars(0); setComment(''); setShowReview(true); }}
          />
        );
      case 'history':
        return (
          <HistoryTab
            history={history}
            busyHistory={busy.history}
            errHistory={err.history}
            focusBookingId={focusBookingId}
            onRate={(item, star) => {
              if (!item.reviewed) {
                setReviewTarget(item);
                setStars(star);
                setComment('');
                setShowReview(true);
              }
            }}
          />
        );
      case 'profile':
        /* Render edit/password forms directly so inputs keep focus when typing. */
        if (edit) {
          return (
            <ProfileEditForm
              profile={profile}
              draft={draft}
              setDraft={setDraft}
              setEdit={setEdit}
              setAvatar={setAvatar}
              setPasswordData={setPasswordData}
              setPasswordErrors={setPasswordErrors}
              setPasswordVisibility={setPasswordVisibility}
              msg={msg}
              saving={saving}
              onSaveProfile={saveProfile}
              t={t}
            />
          );
        }
        if (editPassword) {
          return (
            <ProfilePasswordForm
              passwordData={passwordData}
              setPasswordData={setPasswordData}
              passwordErrors={passwordErrors}
              setPasswordErrors={setPasswordErrors}
              passwordVisibility={passwordVisibility}
              setPasswordVisibility={setPasswordVisibility}
              setEditPassword={setEditPassword}
              msg={msg}
              changingPassword={changingPassword}
              onChangePassword={changePassword}
              t={t}
            />
          );
        }
        return (
          <ProfileTab
            profile={profile}
            busyProfile={busy.profile}
            errProfile={err.profile}
            msg={msg}
            onEditProfile={() => { setEdit(true); setMsg({ ok: '', fail: '' }); }}
            onEditPassword={() => { setEditPassword(true); setMsg({ ok: '', fail: '' }); }}
            showEmailVerification={shouldShowProfileEmailVerification(profile)}
            onEmailVerified={handleEmailVerified}
            lang={lang}
          />
        );
      case 'notification':
        if (notificationsLoading && notifications.length === 0) return <TabLoadingBuffer label={t('dashLoading') || 'Loading...'} />;
        return (
          <NotificationsTab
            list={notifications}
            onMarkAllRead={markAllNotificationsRead}
            onClear={clearAllNotifications}
            onMarkRead={markNotificationRead}
            onRefresh={loadNotifications}
            isRefreshing={notificationsLoading}
            selectedNotification={selectedNotification}
            onNotificationSelect={setSelectedNotification}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="dashboard-container" data-active-tab={tab}>
        {/* NOTE: nav--client ensures mobile bar is scoped and fills width */}
        <Sidebar
          nav={[
            { key:'ehgezly',      title:t('navHome'),          icon:FaHome   },
            { key:'history',      title:t('navHistory'),       icon:FaHistory },
            { key:'profile',      title:t('navProfile'),       icon:FaUser },
            { key:'notification', title:t('navNotifications'), icon:FaBell },
            { key:'logout',       title:t('navLogout'),        icon:FaSignOutAlt  },
          ]}
          active={tab}
          onSelect={setTab}
          onLogout={logout}
          badgeCounts={{ notification: unreadCount }}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <header className={`banner ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
          {!sidebarOpen && (
            <button
              className="sidebar-toggle-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
              title="Open sidebar"
            >
              <FaBars />
            </button>
          )}
          <span>
            {t('dashWelcome')} {profile?.name ? <> <strong className="user-name">{profile.name}</strong></> : null}
          </span>
        </header>

        {/* Booking / notification toast — same design language as Profile updated */}
        {toast && (
          <div className="dashboard-toast-overlay" role="status" aria-live="polite">
            <SuccessBanner
              variant={toast.type === 'warn' ? 'warn' : 'success'}
              className="dashboard-toast-banner"
            >
              {toast.text}
            </SuccessBanner>
          </div>
        )}

        <main 
          className="content-area" 
          style={{ touchAction: 'pan-y' }}
          {...swipeHandlers}
        >
          {renderTab()}
        </main>
      </div>

      <ReviewModal
        show={showReview && !!reviewTarget}
        reviewTarget={reviewTarget}
        lang={lang}
        t={t}
        stars={stars}
        setStars={setStars}
        comment={comment}
        setComment={setComment}
        onSubmit={submitReview}
        onClose={() => setShowReview(false)}
      />
    </>
  );
}

/* Card, Error, Success, Chip, Sidebar are imported from ./dashboard */
